import styled from "@emotion/styled";
import { Box, Drawer, Stack } from "@mui/material";

const BaseDrawerContainer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== "drawerWidth"
})<{ drawerWidth: number }>(({ drawerWidth }) => ({
  "& .MuiDrawer-paper": {
    display: "flex",
    flexDirection: "column",
    padding: "24px",
    width: drawerWidth
  }
}));

const BaseDrawerShadowContainer = styled(Box)({
  height: "0",
  pointerEvents: "none",
  position: "sticky",
  top: "0",
  width: "100%",
  zIndex: "2"
});

const BaseDrawerShadowOverlay = styled(Box, {
  shouldForwardProp: (prop) => prop !== "showShadow"
})<{ showShadow: boolean }>(({ showShadow, theme }) => ({
  background: showShadow ? theme.vars.appEffects.shadow.depth : "none",
  filter: `blur(${theme.vars.appEffects.glass.blurSm})`,
  height: 15,
  opacity: 0.9,
  transition: "background 0.3s ease",
  width: "100%"
}));

const BaseDrawerBody = styled(Stack)({
  flex: 1,
  // 200px reserves vertical space for the header + actions footer chrome
  height: "calc(100% - 200px)",
  justifyContent: "space-between"
});

const BaseDrawerForm = styled("form")({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  height: "100%",
  minHeight: 0
});

const BaseDrawerActions = styled(Stack)({
  alignItems: "center",
  flexDirection: "row",
  gap: 8,
  marginTop: "auto",
  paddingTop: 24
});

/** Outer wrapper: fixed scroll area height (70vh) + anchor for scroll shadow */
const BaseDrawerScrollHeightBox = styled(Box)({
  flex: 1,
  height: "70vh",
  position: "relative"
});

/** The element that actually scrolls — put onScroll here */
const BaseDrawerScrollableBox = styled(Box)({
  height: "100%",
  marginRight: -8,
  overflow: "auto"
});

/** Wraps drawer content; right padding offsets the scrollbar */
const BaseDrawerScrollContentWrapper = styled(Box)({
  // minHeight, not height: at a fixed 100% the wrapper can never be taller
  // than the scroll container, so scrollHeight never exceeds clientHeight and
  // tall content is simply unreachable. minHeight keeps short content filling
  // the panel while letting long content grow and actually scroll.
  minHeight: "100%",
  paddingRight: 8
});

export {
  BaseDrawerContainer,
  BaseDrawerShadowContainer,
  BaseDrawerShadowOverlay,
  BaseDrawerBody,
  BaseDrawerForm,
  BaseDrawerActions,
  BaseDrawerScrollHeightBox,
  BaseDrawerScrollableBox,
  BaseDrawerScrollContentWrapper
};
